/* eslint-disable jsx-a11y/anchor-has-content */
import React, { useEffect, useRef } from 'react'
import ResponsiveImage from '@components/ResponsiveImage'
import { cls } from '@utils/classnames'

import { TeaserSliderAriaLabels, TeaserSliderItem } from '../TeaserSliderTypes'
interface TeaserSliderFeaturedArticleProperties {
  ariaLabels?: TeaserSliderAriaLabels
  containerElement?: HTMLDivElement | null
  item: TeaserSliderItem
  itemIndex: string | number
  isSelected: boolean
  onClick: () => void
}

const TeaserSliderFeaturedArticle: React.FC<TeaserSliderFeaturedArticleProperties> = ({
  onClick,
  item,
  itemIndex,
  isSelected,
  containerElement,
  ariaLabels,
}: TeaserSliderFeaturedArticleProperties) => {
  const reference = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (isSelected && reference.current && containerElement) {
      const containerX = containerElement.getBoundingClientRect().left
      containerElement.scrollTo({ left: reference.current.offsetLeft - containerX, behavior: 'smooth' })
    }
  }, [isSelected])

  const { image, formattedPublicationDate, blogTags, title, pageLink } = item

  const raiseClick = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
    if (!isSelected) {
      if (event.type === 'keydown' && (event as React.KeyboardEvent).key !== 'Enter') {
        return
      }

      onClick()
    }
  }

  return (
    <div
      className={cls({
        'cmp-teaser-slider-featured-article': true,
        'cmp-teaser-slider-featured-article--is-selected': isSelected,
      })}
      ref={reference}
      onClick={raiseClick}
      onKeyDown={raiseClick}
      role="button"
      tabIndex={0}
      aria-labelledby={`featured-article-${itemIndex} featured-article-title-${itemIndex}`}
    >
      <span id={`featured-article-${itemIndex}`} className="sr-only">
        {ariaLabels?.articleDescriptionLabel || 'See description: '}
      </span>
      <link rel="prefetch" href={image.imageUrl || image.originalImageUrl}></link>
      <ResponsiveImage
        src={image.imageUrl || image.originalImageUrl}
        alt={image.altText}
        className="cmp-teaser-slider-featured-article__thumbnail"
      />

      <div className="cmp-teaser-slider-featured-article__content">
        <div className="cmp-teaser-slider-featured-article__meta">
          <span className="sr-only">{ariaLabels?.dateLabel || 'Date: '}</span>
          <div className="cmp-teaser-slider-featured-article__date">{formattedPublicationDate}</div>
          <div className="cmp-blog-tag">
            <span className="sr-only" id={`featured-article-category-${itemIndex}`}>
              {ariaLabels?.tagLabel || 'Categories: '}
            </span>
            <ul className="cmp-blog-tag__list" aria-labelledby={`featured-article-category-${itemIndex}`}>
              <li className="cmp-blog-tag__list-item">{blogTags[0]}</li>
            </ul>
          </div>
        </div>

        <p id={`featured-article-title-${itemIndex}`} className="cmp-teaser-slider-featured-article__title">
          {title}
        </p>
        <a
          href={pageLink.externalizedURL}
          target={pageLink.isTargetBlank ? '_blank' : ''}
          rel={pageLink.isTargetBlank ? 'noopener noreferrer' : ''}
          className="cmp-teaser-slider-featured-article__cta"
          aria-labelledby={`featured-article-title-${itemIndex}`}
        ></a>
      </div>
    </div>
  )
}

export default TeaserSliderFeaturedArticle
